import React, { createContext, useContext } from 'react';
import { noop } from 'lodash';
import useLightbox from '../hooks/useLightbox';
import useLightboxKeyboard from '../hooks/useLightboxKeyboard';
import { LightboxSource } from '../types';

export interface LightboxContextProviderProps {
  initialSilentMode?: boolean;
  sourceInitialIndex: number;
  sources: LightboxSource[];
  onClickNext?: VoidFunction;
  onClickPrev?: VoidFunction;
  onClose: VoidFunction;
  /**
   * Number of items to preload. Use `0` to disable preloading
   */
  itemsToPreload?: number;
}

interface LightboxContextType {
  silentMode: boolean;
  setSilentMode: (silentMode: boolean) => void;
  sources: LightboxContextProviderProps['sources'];
  selectedSourceIndex: number;
  maxSourceIndex: number;
  nextSource: VoidFunction;
  prevSource: VoidFunction;
  close: LightboxContextProviderProps['onClose'];
  itemsToPreload?: number;
}

export const LightboxContext = createContext<LightboxContextType>({} as LightboxContextType);

export const LightboxContextProvider = ({
  initialSilentMode,
  sourceInitialIndex,
  sources,
  onClose,
  onClickNext = noop,
  onClickPrev = noop,
  itemsToPreload,
  children,
}: React.PropsWithChildren<LightboxContextProviderProps>) => {
  const { selectedSourceIndex, maxSourceIndex, nextSource, prevSource, silentMode, setSilentMode } = useLightbox({
    initialSilentMode,
    sourceInitialIndex,
    sources,
    onClickNext,
    onClickPrev,
    itemsToPreload,
  });

  useLightboxKeyboard({ nextSource, prevSource, onClose });

  return (
    <LightboxContext.Provider
      value={{
        silentMode,
        setSilentMode,
        selectedSourceIndex,
        maxSourceIndex,
        nextSource,
        prevSource,
        sources,
        close: onClose,
        itemsToPreload,
      }}
    >
      {children}
    </LightboxContext.Provider>
  );
};

export const useLightboxContext = () => useContext(LightboxContext);
